import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    IconButton,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import {
    Add as AddIcon,
    Search,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MailOutline,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import { buildShortDescription, openTemplatePreviewWindow } from "../../utils/dummyEmailTemplatesStore";
import base_url from "../../constants";
import useNotification from "../common/Notification";

const TABLE_SURFACE_SX = {
    borderRadius: 2,
    border: "1px solid rgba(15,23,42,0.08)",
    bgcolor: "#fff",
    boxShadow: "0 4px 18px rgba(15,23,42,0.05)",
    overflow: "hidden",
};

const TABLE_HEAD_CELL_SX = {
    fontWeight: 800,
    color: "text.primary",
    bgcolor: "#f8fafc",
    borderBottom: "1px solid",
    borderColor: "divider",
    py: 1.5,
};

const ROWS_PER_PAGE = 8;

const formatCreatedDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
};

const formatTypeLabel = (type) => {
    const value = String(type ?? "0");
    if (value === "1") return "Raw HTML";
    return "Simple";
};

const mapApiTemplate = (item) => ({
    id: item.id,
    name: item.template_title || "Untitled Template",
    preview: buildShortDescription(item.template),
    type: formatTypeLabel(item.type ?? item.template_type),
    createdDate: formatCreatedDate(item.created_at),
    html: item.template || "",
});

const fieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: "#fff",
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
            borderWidth: 2,
        },
    },
};

const TemplatePreviewThumb = ({ html, onClick }) => {
    const previewHtml = String(html || "").trim();

    return (
        <Box
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onClick?.(event);
                }
            }}
            sx={{
                position: "relative",
                width: 120,
                height: 80,
                borderRadius: 1.5,
                bgcolor: "#f4f7fb",
                border: "1px solid rgba(15,23,42,0.08)",
                overflow: "hidden",
                cursor: previewHtml ? "pointer" : "default",
                transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                "&:hover": previewHtml
                    ? {
                          borderColor: "primary.main",
                          boxShadow: "0 4px 14px rgba(25,118,210,0.18)",
                      }
                    : undefined,
            }}
        >
            {previewHtml ? (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            width: "200%",
                            height: "200%",
                            transform: "scale(0.5)",
                            transformOrigin: "top left",
                            p: 1.5,
                            pointerEvents: "none",
                            bgcolor: "#fff",
                        }}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.disabled",
                    }}
                >
                    <MailOutline fontSize="small" />
                </Box>
            )}
        </Box>
    );
};

const truncateText = (text, maxLength) => {
    const value = String(text || "");
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength).trim()}...`;
};

const DummyTemplateList = () => {
    const navigate = useNavigate();
    const notify = useNotification();
    const userId = Cookies.get("userId");
    const userRole = Cookies.get("userRole")?.split(",")[0];
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);

    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(base_url + "/email-template/getbyroleanduserid", {
                params: {
                    user_id: userId,
                    role: userRole,
                },
            });
            const list = Array.isArray(response.data?.data) ? response.data.data : [];
            setTemplates(list.map(mapApiTemplate));
        } catch (error) {
            console.error("Error fetching templates:", error);
            notify("Failed to fetch templates", "error");
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    }, [userId, userRole, notify]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const filteredTemplates = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return templates;

        return templates.filter(
            (template) =>
                String(template.name || "").toLowerCase().includes(q) ||
                String(template.preview || "").toLowerCase().includes(q) ||
                String(template.createdDate || "").toLowerCase().includes(q)
        );
    }, [query, templates]);

    const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / ROWS_PER_PAGE));
    const paginatedTemplates = filteredTemplates.slice(
        (page - 1) * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE
    );

    const openCreateDialog = () => {
        navigate("/email/CreateNewTemplate");
    };

    const openEditTemplate = (template) => {
        navigate(`/email/CreateNewTemplate?editId=${template.id}`, {
            state: { templateData: template },
        });
    };

    const handlePreviewTemplate = (template) => {
        if (!String(template.html || "").trim()) return;
        openTemplatePreviewWindow(template.html, template.name || "Email Template Preview");
    };

    const handleDelete = async (id) => {
        try {
            await axios.post(base_url + "/email-template/delete", {
                template_id: id,
                user_id: userId,
                role: userRole,
            });
            notify("Template deleted successfully", "success");
            fetchTemplates();
        } catch (error) {
            console.error("Error deleting template:", error);
            notify("Failed to delete template", "error");
        }
    };

    return (
        <Box sx={{ ...WB_SX.page, p: 0 }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "flex-end" },
                    gap: 2,
                    mb: 2.5,
                }}
            >
                <Box sx={{ maxWidth: { xs: "100%", md: "760px" } }}>
                    <Typography
                        variant="h5"
                        sx={{
                            ...WB_UI.typography.pageTitle,
                            color: "text.primary",
                        }}
                    >
                        Email Templates
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        Create and manage email templates for your business campaigns.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                        }}
                    >
                        Create Template
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ ...TABLE_SURFACE_SX, mb: 2 }}>
                <Box
                    sx={{
                        p: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#fff",
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search template name or description…"
                        value={query}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={fieldSx}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ bgcolor: "#fff" }}>
                            <Table size="small" stickyHeader sx={{ bgcolor: "#fff" }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                        {["Name", "Short Description", "Preview", "Created At", "Action"].map(
                                            (heading) => (
                                            <TableCell
                                                key={heading}
                                                align={heading === "Action" ? "center" : "left"}
                                                sx={TABLE_HEAD_CELL_SX}
                                            >
                                                {heading}
                                            </TableCell>
                                            )
                                        )}
                                    </TableRow>
                                </TableHead>

                                <TableBody sx={{ bgcolor: "#fff" }}>
                                    {paginatedTemplates.length === 0 ? (
                                        <TableRow sx={{ bgcolor: "#fff" }}>
                                            <TableCell
                                                colSpan={5}
                                                align="center"
                                                sx={{ py: 5, bgcolor: "#fff" }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {query
                                                        ? "No templates found matching your search"
                                                        : "No templates found"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedTemplates.map((template) => (
                                            <TableRow
                                                key={template.id}
                                                hover
                                                sx={{
                                                    bgcolor: "#fff",
                                                    "& td": {
                                                        borderBottom: "1px solid",
                                                        borderColor: "divider",
                                                        py: 1.8,
                                                        bgcolor: "#fff",
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "rgba(25,118,210,0.03)",
                                                        "& td": {
                                                            bgcolor: "rgba(25,118,210,0.03)",
                                                        },
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 700, color: "text.primary" }}
                                                    >
                                                        {template.name}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {truncateText(template.preview, 38)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Tooltip title="Open preview in new tab">
                                                        <span>
                                                            <TemplatePreviewThumb
                                                                html={template.html}
                                                                onClick={() =>
                                                                    handlePreviewTemplate(template)
                                                                }
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" color="text.primary">
                                                        {template.createdDate}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openEditTemplate(template)}
                                                                sx={{ color: "primary.main" }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDelete(template.id)}
                                                                sx={{ color: "error.main" }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                px: 2,
                                py: 1.5,
                                flexWrap: "wrap",
                                borderTop: "1px solid",
                                borderColor: "divider",
                                bgcolor: "#fff",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Showing{" "}
                                {filteredTemplates.length === 0
                                    ? 0
                                    : (page - 1) * ROWS_PER_PAGE + 1}
                                -
                                {Math.min(page * ROWS_PER_PAGE, filteredTemplates.length)} of{" "}
                                {filteredTemplates.length} templates
                            </Typography>

                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                                shape="rounded"
                                size="small"
                            />
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default DummyTemplateList;
